import { useCallback, useRef, useState } from 'react';

import { useIntersectObserver } from './useIntersectObserver';

export const useMultipleOnceReveal = (options?: Partial<IntersectionObserverInit>) => {
  const [revealedIndices, setRevealedIndices] = useState<Set<number>>(new Set());
  const elementsRef = useRef<(Element | null)[]>([]);

  const handleIntersect = useCallback((entry: IntersectionObserverEntry, observer: IntersectionObserver) => {
    if (entry.isIntersecting) {
      const index = elementsRef.current.indexOf(entry.target);
      if (index !== -1) {
        setRevealedIndices((prev) => new Set(prev).add(index));
      }
      observer.unobserve(entry.target);
    }
  }, []);

  const { observe, unobserve } = useIntersectObserver(handleIntersect, options);

  const setTargetRef = useCallback(
    (index: number) => (element: Element | null) => {
      if (element) {
        elementsRef.current[index] = element;
        unobserve(element);
        observe(element);
      }
    },
    [observe, unobserve],
  );

  const isRevealed = (index: number) => revealedIndices.has(index);

  return { setTargetRef, isRevealed, revealedIndices };
};
